// Calendarul săptămânal al elevului (/student/calendar) și al instructorului (/instructor) — din același server ca
// calendarul nou: GET /api/calendar?view=personal (lecțiile mele; elevul — și examenele practice; instructorul — și
// zilele în care înlocuiește). Evenimentele au exact forma de până acum (aceleași cartonașe și aceeași fereastră),
// doar sursa e alta: înainte elevul făcea 3 cereri, iar instructorul încerca /reservations/filter, apoi
// /reservations/instructor, apoi toate lecțiile școlii (/reservations/all).

import { fetchCalendar } from "../calendarV2/calendarV2Api";
import { buildLessonCardItem, buildPracticalCardItem, readPracticalStartValue } from "./studentReservationCardUtils";
import { mapReservationsToEvents, toFloatingDate } from "./studentCalendarUtils";

/** Cât se vede în calendarul săptămânal: 90 de zile înapoi și înainte (ca până acum). */
export const PERSONAL_RANGE_DAYS = 90;
/** Serverul dă cel mult 62 de zile într-o cerere. */
const CHUNK_DAYS = 61;

const pad = (n) => String(n).padStart(2, "0");
export const localDayKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

function addDaysKey(day, n) {
   const [y, m, d] = day.split("-").map(Number);
   return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}

/** Bucăți de cel mult 61 de zile care acoperă [fromDay, toDay]. */
export function rangeChunks(fromDay, toDay, chunk = CHUNK_DAYS) {
   const out = [];
   let start = fromDay;
   while (start <= toDay) {
      const end = addDaysKey(start, chunk - 1);
      out.push({ from: start, to: end < toDay ? end : toDay });
      start = addDaysKey(end, 1);
   }
   return out;
}

const GEARBOX_LABEL = { manual: "Manual", automatic: "Automat" };

/** Lecția de la server → forma rezervării pe care o citesc cartonașele (ora „de perete” scrisă ca UTC, ca în bază). */
export function lessonToRaw(lesson) {
   const time = lesson?.storedTime || lesson?.time;
   if (!lesson?.date || !time) return null;
   return {
      id: lesson.id,
      startTime: `${lesson.date}T${time}:00.000Z`,
      isCancelled: lesson.status === "cancelled",
      isConfirmed: Boolean(lesson.isConfirmed),
      sector: lesson.sector || "",
      gearbox: GEARBOX_LABEL[lesson.gearbox] || "",
      instructorId: lesson.instructorId ?? null,
      instructor: lesson.instructor || null,
      lessonNumber: lesson.lessonNumber ?? null,
   };
}

/** Locul elevului la examenul practic → forma de până acum (my-reservation / my-history). */
export function examSeatToRaw(slot, seat) {
   return {
      id: seat.id,
      status: seat.status,
      date: slot.date,
      time: slot.time,
      sector: slot.sector || "",
      gearbox: GEARBOX_LABEL[slot.gearbox] || "",
   };
}

/**
 * Evenimentele calendarului săptămânal din răspunsurile serverului.
 * Instructorul: lecțiile lui și cele pe care le predă ca înlocuitor (nu și restul lecțiilor celui înlocuit).
 */
export function personalEventsFromCalendar(responses) {
   const lessons = new Map();
   const exams = new Map();
   for (const resp of responses || []) {
      const viewer = resp?.viewer || {};
      const me = viewer.instructorId ?? null;
      for (const lesson of resp?.lessons || []) {
         if (viewer.role === "INSTRUCTOR" && me != null && lesson.instructorId !== me && lesson.substituteInstructorId !== me) continue;
         const raw = lessonToRaw(lesson);
         if (raw) lessons.set(raw.id, raw);
      }
      if (viewer.role === "USER") {
         for (const slot of resp?.exams || []) {
            for (const seat of slot.seats || []) {
               if (seat && seat.studentId === viewer.userId) exams.set(seat.id, examSeatToRaw(slot, seat));
            }
         }
      }
   }

   const lessonEvents = mapReservationsToEvents([...lessons.values()]).map((event, index) => ({
      ...event,
      kind: "lesson",
      typeLabel: "Lecție",
      title: "Lecție",
      cardItem: buildLessonCardItem(event.raw, index),
   }));

   const examEvents = [...exams.values()]
      .map((raw, index) => {
         const cardItem = buildPracticalCardItem(raw, index, { current: raw.status === "SCHEDULED" });
         const start = toFloatingDate(readPracticalStartValue(raw));
         if (!cardItem || !start) return null;
         return {
            id: cardItem.id,
            kind: "practicalExam",
            typeLabel: "Examen",
            title: "Examen",
            start,
            end: new Date(start.getTime() + 60 * 60 * 1000),
            sector: cardItem.sector,
            gearbox: cardItem.gearbox,
            raw,
            cardItem,
         };
      })
      .filter(Boolean);

   return [...lessonEvents, ...examEvents];
}

/** Încarcă ±90 de zile (3 cereri de cel mult 61 de zile, în paralel). */
export async function loadPersonalCalendarEvents(now = new Date()) {
   const today = localDayKey(now);
   const chunks = rangeChunks(addDaysKey(today, -PERSONAL_RANGE_DAYS), addDaysKey(today, PERSONAL_RANGE_DAYS));
   // și lecțiile anulate (ca până acum, gri); serverul lasă doar ce e permis rolului (examenele — elevului și examinatorului)
   const include = "lessons,cancelled,exams,substitutions";
   const responses = await Promise.all(chunks.map((c) => fetchCalendar({ from: c.from, to: c.to, view: "personal", include })));
   return personalEventsFromCalendar(responses);
}
